import type { Operation, RequestBody, ResponseBody } from '@platform/openapi';

export type CategoryList = ResponseBody<Operation<'/admin/categories', 'get'>>;
export type Category = CategoryList['items'][number];
export type CategoryStatus = Category['status'];
export type CreateCategoryBody = RequestBody<Operation<'/admin/categories', 'post'>>;
export type UpdateCategoryBody = RequestBody<Operation<'/admin/categories/{id}', 'patch'>>;

export type CategoryNode = { readonly category: Category; readonly depth: number };

export type LocalizedName = { readonly locale: string; readonly name: string };

export type Named = { readonly translations: readonly LocalizedName[] };

export const pickName = (names: readonly LocalizedName[], locale: string, fallback: string): string =>
  (names.find((entry) => entry.locale === locale) ?? names[0])?.name ?? fallback;

export const nameIn = (named: Named, locale: string, fallback: string): string =>
  pickName(named.translations, locale, fallback);

export const asTree = (categories: readonly Category[]): readonly CategoryNode[] => {
  const byParent = new Map<string | null, Category[]>();
  for (const category of categories) {
    const siblings = byParent.get(category.parentId) ?? [];
    siblings.push(category);
    byParent.set(category.parentId, siblings);
  }
  for (const siblings of byParent.values()) siblings.sort((a, b) => a.position - b.position);

  const walk = (parentId: string | null, depth: number): readonly CategoryNode[] =>
    (byParent.get(parentId) ?? []).flatMap((category) => [
      { category, depth },
      ...walk(category.id, depth + 1),
    ]);

  const rooted = walk(null, 0);
  const placed = new Set(rooted.map((node) => node.category.id));
  const orphans = categories
    .filter((category) => !placed.has(category.id))
    .map((category) => ({ category, depth: 0 }));
  return [...rooted, ...orphans];
};

export const parentIdsOf = (categories: readonly Category[]): ReadonlySet<string> =>
  new Set(categories.flatMap((category) => (category.parentId === null ? [] : [category.parentId])));

const isHiddenBy = (category: Category, byId: ReadonlyMap<string, Category>, folded: ReadonlySet<string>) => {
  let parentId = category.parentId;
  while (parentId !== null) {
    if (folded.has(parentId)) return true;
    parentId = byId.get(parentId)?.parentId ?? null;
  }
  return false;
};

export const unfoldedNodes = (
  nodes: readonly CategoryNode[],
  folded: ReadonlySet<string>,
): readonly CategoryNode[] => {
  const byId = new Map(nodes.map((node) => [node.category.id, node.category]));
  return nodes.filter((node) => !isHiddenBy(node.category, byId, folded));
};

const comparable = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase();

export const matchingNodes = (nodes: readonly CategoryNode[], search: string): readonly CategoryNode[] => {
  const wanted = comparable(search.trim());
  if (wanted === '') return nodes;
  return nodes.filter((node) =>
    node.category.translations.some(
      (translation) => comparable(translation.name).includes(wanted) || translation.slug.includes(wanted),
    ),
  );
};

export type TranslationCoverage = {
  readonly locale: string;
  readonly translated: number;
  readonly total: number;
};

export const translationCoverage = (
  categories: readonly Category[],
  locales: readonly string[],
): readonly TranslationCoverage[] =>
  locales.map((locale) => ({
    locale,
    translated: categories.filter((category) =>
      category.translations.some((translation) => translation.locale === locale),
    ).length,
    total: categories.length,
  }));

const descendantsOf = (categoryId: string, categories: readonly Category[]): readonly string[] =>
  categories
    .filter((category) => category.parentId === categoryId)
    .flatMap((child) => [child.id, ...descendantsOf(child.id, categories)]);

export const countsWithDescendants = (
  categories: readonly Category[],
  counts: ReadonlyMap<string, number>,
): ReadonlyMap<string, number> =>
  new Map(
    categories.map((category) => [
      category.id,
      [category.id, ...descendantsOf(category.id, categories)].reduce(
        (total, id) => total + (counts.get(id) ?? 0),
        0,
      ),
    ]),
  );
